import { useMutation } from '@apollo/client';
import Box from '@risk-smart/themed-cloudscape-components/box';
import FormField from '@risk-smart/themed-cloudscape-components/form-field';
import Modal from '@risk-smart/themed-cloudscape-components/modal';
import SpaceBetween from '@risk-smart/themed-cloudscape-components/space-between';
import Button from '@risksmart-app/components/src/button';
import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import {
  Appetite_Model_Enum,
  RecalculateAppetitesDocument,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { type FC, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ControlledJsonEditor } from 'src/components/form/controlled-json-editor/ControlledJsonEditor';
import ControlledSelect from 'src/components/form/controlled-select';
import { useIsCustomerSupport } from 'src/hooks/useIsCustomerSupport';

export const AppetiteCascadingSettings: FC = () => {
  const { control } = useFormContext();
  const { t } = useTranslation('common', { keyPrefix: 'modules.fields' });
  const { t: ct } = useTranslation('common');
  const isCustomerSupport = useIsCustomerSupport();
  const { addNotification } = useNotifications();
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const cascadingModel = useWatch({ control, name: 'AppetiteCascadingModel' });
  const cascadeEnabled = cascadingModel === Appetite_Model_Enum.TopDownCascade;

  const [recalculateAppetites, { loading: recalculating }] = useMutation(
    RecalculateAppetitesDocument
  );

  const handleRecalculate = async () => {
    setShowConfirmModal(false);
    try {
      await recalculateAppetites();
      addNotification({
        type: 'success',
        content: t('RecalculateAppetiteCascade_success'),
      });
    } catch {
      addNotification({
        type: 'error',
        content: t('RecalculateAppetiteCascade_error'),
      });
    }
  };

  return (
    <SpaceBetween size={'s'}>
      <FormField>
        <ControlledSelect
          testId={'appetiteCascadingModel'}
          control={control}
          name={'AppetiteCascadingModel'}
          label={t('AppetiteCascadingModel')}
          options={[
            {
              label: t('AppetiteCascadingModels.default'),
              value: Appetite_Model_Enum.Default,
            },
            {
              label: t('AppetiteCascadingModels.top_down_cascade'),
              value: Appetite_Model_Enum.TopDownCascade,
            },
          ]}
        />
      </FormField>
      <FormField>
        <ControlledJsonEditor
          control={control}
          name={'AppetiteCascadingModelConfig'}
          label={t('AppetiteCascadingModelConfig')}
        />
      </FormField>
      {isCustomerSupport && cascadeEnabled && (
        <>
          <Button
            data-testid={'recalculate-appetite-cascade-button'}
            variant={'normal'}
            onClick={() => setShowConfirmModal(true)}
            loading={recalculating}
            disabled={recalculating}
          >
            {t('RecalculateAppetiteCascade')}
          </Button>
          <Modal
            data-testid={'recalculate-appetite-cascade-modal'}
            visible={showConfirmModal}
            onDismiss={() => setShowConfirmModal(false)}
            header={t('RecalculateAppetiteCascade_confirm_header')}
            closeAriaLabel={ct('closeModal')}
            size={'small'}
            footer={
              <Box float={'right'}>
                <SpaceBetween direction={'horizontal'} size={'xs'}>
                  <Button
                    variant={'normal'}
                    onClick={() => setShowConfirmModal(false)}
                  >
                    {ct('cancel')}
                  </Button>
                  <Button variant={'primary'} onClick={handleRecalculate}>
                    {t('RecalculateAppetiteCascade_confirm_button')}
                  </Button>
                </SpaceBetween>
              </Box>
            }
          >
            {t('RecalculateAppetiteCascade_confirm_body')}
          </Modal>
        </>
      )}
    </SpaceBetween>
  );
};
